import React from 'react';
import _omitBy from 'lodash/omitBy';
import _isUndefined from 'lodash/isUndefined';
import i18n from './i18n';
import _get from 'lodash/get';
import _lastIndexOf from 'lodash/lastIndexOf';
import langUtils from './langUtils';
import dayjs from 'dayjs';
import moment from 'moment';
import {solutionUtils} from './solutionUtils';

const utils = {
  omit: v => {
    const vv = _omitBy(v, _isUndefined);
    return _omitBy(vv, v => !v);
  },
  isUrl: url => {
    const Expression = /http(s)?:\/\/([\w-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?/;
    const objExp = new RegExp(Expression);
    return objExp.test(url) === true;
  },
  hexToRgb: hex => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return r
      ? {
          r: parseInt(r[1], 16),
          g: parseInt(r[2], 16),
          b: parseInt(r[3], 16),
        }
      : '';
  },
  rgbToHex: ({r, g, b}) =>
    '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1),
  getOptionName: (options, val) => {
    const obj = options.find(o => o.value === val) || null;
    return obj == null ? '' : obj.label;
  },
  redirectToSignInUrl: lang =>
    (window.location.href =
      utils.getUrl() +
      langUtils.convertToFrontendLangUrl(lang) +
      '/business/user/sign-in?next=' +
      encodeURIComponent(window.location.href)),
  frontendLanguage: () => {
    return i18n.language === 'ja' ? '/ja' : '';
  },
  documentCenterLanguage: () => {
    return i18n.language === 'ja' ? 'ja' : 'en';
  },
  isMakeupArCom: () => window.location.hostname.indexOf('makeupar.com') !== -1,
  isCnSite: auth =>
    window.location.hostname.includes('perfectcorp.cn') ||
    _get(auth, 'customer.country') === 'CN',
  getUrl: (type = 'frontend') => {
    switch (type) {
      case 'sdk':
        return utils.isMakeupArCom()
          ? process.env.REACT_APP_MAKEUPAR_SDK_URL
          : process.env.REACT_APP_SDK_URL;
      case 'backend':
        if (utils.isMakeupArCom()) {
          return process.env.REACT_APP_MAKEUPAR_BACKEND_URL;
        } else if (utils.isCnSite()) {
          return process.env.REACT_APP_CN_BACKEND_URL;
        } else {
          return process.env.REACT_APP_BACKEND_URL;
        }
      case 'stage':
        return utils.isMakeupArCom()
          ? process.env.REACT_APP_STAGE_MAKEUPAR_FRONTEND_URL
          : process.env.REACT_APP_STAGE_FRONTEND_URL;
      case 'epf':
        return process.env.REACT_APP_EPF_URL;
      default:
        if (utils.isMakeupArCom()) {
          return process.env.REACT_APP_MAKEUPAR_FRONTEND_URL;
        } else if (utils.isCnSite()) {
          return process.env.REACT_APP_CN_FRONTEND_URL;
        } else {
          return process.env.REACT_APP_FRONTEND_URL;
        }
    }
  },
  getDomain: () => {
    if (window.location.hostname.indexOf('perfectcorp') !== -1) {
      return 'perfectcorp';
    }
    return 'makeupar';
  },
  getUserAgentInApp: index => {
    if (typeof window === 'undefined' && !_get(window, 'navigator.userAgent'))
      return '';
    if (
      window.navigator.userAgent.indexOf('com.perfectcorp.ymkbusiness') ===
        -1 &&
      window.navigator.userAgent.indexOf('com.perfectcorp.en.ymkbusiness') ===
        -1
    )
      return 'notInApps';
    return window.navigator.userAgent
      .slice(
        _lastIndexOf(window.navigator.userAgent, '(') + 1,
        _lastIndexOf(window.navigator.userAgent, ')')
      )
      .split(' ')[index];
  },
  isWithGa: () => {
    return (
      utils.getUserAgentInApp() === 'notInApps' ||
      utils.getUserAgentInApp(3) === 'withga'
    );
  },
  isInYmkApp: () => {
    return (
      utils.getUserAgentInApp(0) === 'com.perfectcorp.ymkbusiness' ||
      utils.getUserAgentInApp(0) === 'com.perfectcorp.en.ymkbusiness'
    );
  },
  isModuleItaSupported: auth =>
    _get(auth, 'customer.isI18nItaSupported', false),
  customerInfo: auth => {
    return {
      token: _get(auth, 'token', ''),
      customerId: _get(auth, 'customer.id', ''),
      brandInfo: _get(auth, 'brandInfo', ''),
    };
  },
  isScpActivationAllowed: auth => {
    return _get(auth, 'brandInfo.status', 'success') === 'success';
  },
  isShowNewLabel: (auth, day) => {
    if (solutionUtils.isOnlySimulation(auth)) {
      return false;
    }
    const brandInfo = _get(auth, 'brandInfo', '');
    const isoDate = _get(brandInfo, 'lastUpgradeTime', '');
    const isVersionAbove2AndHd =
      _get(brandInfo, 'templateVersion', 1) >= 2 &&
      _get(brandInfo, 'recommendationAbility', 'SD') === 'HD';
    if (!isVersionAbove2AndHd || !brandInfo || !isoDate) {
      return false;
    }

    return (
      moment().unix() <
      dayjs(isoDate)
        .add(day, 'day')
        .unix()
    );
  },
  isShowNewLabelForOldVersion: () => {
    return (
      moment().unix() <
      dayjs('2025-05-06') //gm day
        .add(14, 'day')
        .unix()
    );
  },
  getCustomerName: auth => _get(auth, 'customer.customerName', ''),
  camelToSnake4Keys: camelObj => {
    const snakeObj = {};
    for (const key in camelObj) {
      if (camelObj.hasOwnProperty(key)) {
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        snakeObj[snakeKey] = camelObj[key];
      }
    }
    return snakeObj;
  },
  snakeToCamel4Values: (camelObj, keys = []) => {
    keys.forEach(key => {
      if (camelObj.hasOwnProperty(key)) {
        camelObj[key] = camelObj[key].replace(/_([a-z])/g, function(
          match,
          group1
        ) {
          return group1.toUpperCase();
        });
      }
    });
    return camelObj;
  },
  formatTimeParam: (paramObj, paramKeys = []) => {
    paramKeys.forEach(paramKey => {
      if (paramObj[paramKey]) {
        paramObj[paramKey] = new Date(paramObj[paramKey]).toISOString();
      }
    });
    return paramObj;
  },
  mapToKeys(obj, mapping) {
    const mappedObj = {};
    if (!obj || Object.keys(obj).length === 0) {
      return {};
    }

    Object.keys(obj).forEach(key => {
      if (mapping[key]) {
        mappedObj[mapping[key]] = obj[key];
      } else {
        mappedObj[key] = obj[key];
      }
    });
    return mappedObj;
  },
  mapToCategory: item => ({
    label: item.name,
    value: item.id,
  }),
  clearPrevAPI: (id, api, timeout = 500) => {
    clearTimeout(id);
    return setTimeout(() => {
      api();
    }, timeout);
  },
  getFunctionTypeI18n: functionType => {
    switch (functionType) {
      case 'MAKEUP':
        return i18n.t('c.l.sidebar.1100.makeup.title', 'Makeup');
      case 'EYEWEAR':
        return i18n.t('c.l.sidebar.1100.eyewear.title', 'Eyewear');
      case 'SKINCARE_PRO':
        return i18n.t('c.l.sidebar.1100.skincarepro.title', 'Skincare Pro');
      case 'SKIN_ANALYSIS':
        return i18n.t('u.s.skincare.skin.analysis', 'Skin Analysis');
      case 'AESTHETIC_SIMULATION':
        return i18n.t(
          'u.s.skincare.aesthetic.simulation',
          'Aesthetic Simulation'
        );
      default:
        return;
    }
  },
  getPageModeFormPath: location => {
    const pathname = _get(location, 'pathname', '');
    const isAdd = pathname.includes('/add');
    const isEdit = pathname.includes('/edit/');
    const isCopy = pathname.includes('/copy/');
    const isView = pathname.includes('/view/');

    return {isAdd, isEdit, isCopy, isView};
  },
};

export default utils;
